<template>
    <div></div>
</template>
<script>
import makeElementDraggable from "@/utils/make-element-draggable";
import { onHide, onShow } from "@intercom/messenger-js-sdk";

export default {
    data() {
        return {
            intercomDialogDraggable: null
        };
    },
    computed: {
        isTouchDevice() {
            const isTouch =
                "ontouchstart" in window || navigator.maxTouchPoints > 0; // Any device using touch screen
            const isSmallScreen = window.matchMedia("(max-width: 1024px)")
                .matches; // Dekstop smallest size
            return isTouch && isSmallScreen; // For touch screen and not desktop size is not draggable
        }
    },
    mounted() {
        this._getSetting().then(() => {
            if (!this.isTouchDevice) {
                onShow(() => this.initDraggable());
                onHide(() => this.destroyDraggable());
            }
        });
    },
    beforeDestroy() {
        this.destroyDraggable();
    },
    methods: {
        async waitForElement(selector, timeout = 5000) {
            // limit is 5s, if more, the intercom will not apply the draggable
            const pollInterval = 500;
            const startTime = Date.now();

            while (Date.now() - startTime < timeout) {
                const element = document.querySelector(selector);
                if (element) {
                    return element;
                }
                await this._sleep(pollInterval);
            }
            return null;
        },
        async initDraggable() {
            const messengerFrame = await this.waitForElement(
                ".intercom-messenger-frame"
            );
            const iframe = await this.waitForElement(
                'iframe[name="intercom-messenger-frame"]'
            );

            if (!messengerFrame) {
                return;
            }

            this.setIntercomPosition(messengerFrame);

            // Update the min height of the parent component and iframe height to prevent the footer of the intercom iframe hidden when adding the header
            const headerHeight = 30; // Height of the draggable intercom header
            messengerFrame.style.minHeight = `min(704px, 100% - 40px + ${headerHeight}px);`;

            if (iframe) {
                iframe.style.height = `calc(100% - ${headerHeight}px)`; // Subtract header height
            }

            const draggableHeaderId = "intercom-draggable-header";
            let draggableHeader = document.querySelector(
                `div#${draggableHeaderId}`
            );

            if (!draggableHeader) {
                // Create a draggable header div
                // The intercom using iframe, so we cannot use the element in the iframe
                // Using draggable header not icon as, when use position=absoulute, the dragging function will break
                const headerHTML = `
                    <div id="${draggableHeaderId}" class="draggable-header" style="height: ${headerHeight}px;">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                            <path d="M278.6 9.4c-12.5-12.5-32.8-12.5-45.3 0l-64 64c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l9.4-9.4L224 224l-114.7 0 9.4-9.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-64 64c-12.5 12.5-12.5 32.8 0 45.3l64 64c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-9.4-9.4L224 288l0 114.7-9.4-9.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l64 64c12.5 12.5 32.8 12.5 45.3 0l64-64c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-9.4 9.4L288 288l114.7 0-9.4 9.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l64-64c12.5-12.5 12.5-32.8 0-45.3l-64-64c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l9.4 9.4L288 224l0-114.7 9.4 9.4c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-64-64z"/>
                        </svg>
                    </div>
                `;

                // Insert the generated header above the iframe
                const headerElement = document.createElement("div");
                headerElement.innerHTML = headerHTML.trim();
                draggableHeader = headerElement.firstChild;
                messengerFrame.insertBefore(
                    draggableHeader,
                    messengerFrame.firstChild
                );
            }

            this.intercomDialogDraggable = makeElementDraggable(
                messengerFrame, // The container to move
                draggableHeader, // The draggable header
                {
                    makeContentFixed: true,
                    movement: "auto",
                    moveUpCallbackFunction: this.onMoveUp,
                    isPreventHeaderOffScreen: true
                }
            );
        },
        setIntercomPosition(messengerFrame) {
            let intercomPosition = null;
            const intercomPositionLocalStorage = localStorage.getItem(
                "intercom_position"
            );
            if (intercomPositionLocalStorage) {
                try {
                    intercomPosition = JSON.parse(intercomPositionLocalStorage);
                    if (
                        intercomPosition &&
                        intercomPosition.top !== undefined &&
                        intercomPosition.left !== undefined
                    ) {
                        messengerFrame.style.top = intercomPosition.top;
                        messengerFrame.style.left = intercomPosition.left;
                    }
                } catch (e) {
                    console.error(e);
                }
            }
        },
        onMoveUp() {
            const messengerFrame = document.querySelector(
                ".intercom-messenger-frame"
            );

            if (
                messengerFrame &&
                messengerFrame.style.top !== undefined &&
                messengerFrame.style.left !== undefined
            ) {
                localStorage.setItem(
                    "intercom_position",
                    JSON.stringify({
                        top: messengerFrame.style.top,
                        left: messengerFrame.style.left
                    })
                );
            }
        },
        destroyDraggable() {
            if (this.intercomDialogDraggable) {
                this.intercomDialogDraggable();
                this.intercomDialogDraggable = null; // Clean up reference
            }
        }
    }
};
</script>
<style>
.intercom-messenger-frame > div.draggable-header {
    cursor: move;
    background-color: rgb(51, 51, 51);
    display: flex;
    align-items: center;
    padding-left: 10px;
}
.intercom-messenger-frame > div.draggable-header > svg {
    cursor: move;
    width: 12px;
    height: 12px;
}
.intercom-messenger-frame > div.draggable-header > svg > path {
    fill: white;
}
</style>
